import * as React from "react"
import { Head, Link, useForm, usePage } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { TargetPicker } from "@/components/automations/TargetPicker"
import { MessageEditor, messageBlocksSave } from "@/components/automations/MessageEditor"
import { AudiencePreview } from "@/components/automations/AudiencePreview"
import { UnionWideNotice } from "@/components/automations/UnionWideNotice"
import { useAudiencePreview } from "@/components/automations/use-audience-preview"
import { ScheduleFields } from "@/components/automations/ScheduleFields"
import { NotificationRecipients } from "@/components/automations/NotificationRecipients"
import { RehearsalMode } from "@/components/automations/RehearsalMode"
import {
  LanguageVersions,
  draftsForSetup,
  languagesBlockSave,
  setupPayload,
  type LanguageDraft,
} from "@/components/automations/LanguageVersions"
import { Button } from "@/components/ui/button"
import { CheckboxOption } from "@/components/ui/checkbox"
import { Combobox } from "@/components/ui/combobox"
import { Input } from "@/components/ui/input"
import { useBlockAutoReload } from "@/hooks/use-block-auto-reload"
import type { PageProps } from "@/types/inertia"
import {
  type AutomationSchedule,
  type AutomationSetup,
  type LanguageOptions,
} from "@/types/automation"

export default function AutomationSetupPage({
  automation,
  language_options,
  can_edit,
}: {
  automation: AutomationSetup
  /** Deferred: undefined until the roster has been asked. */
  language_options?: LanguageOptions
  /**
   * False with Read only on Automations: every setting and the preview, and no
   * control that changes anything. The server refuses a save regardless.
   */
  can_edit: boolean
}) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const readOnly = !can_edit

  const { data, setData, transform, patch, processing, isDirty } = useForm({
    is_enabled: automation.is_enabled,
    is_rehearsal: automation.is_rehearsal,
    label: automation.label ?? "",
    custom_message: automation.custom_message ?? automation.default_message,
    send_time: automation.send_time,
    time_zone: automation.time_zone,
    covers_all_locals: automation.covers_all_locals,
    selected_target_ids: automation.selected_target_ids,
    frequency: automation.frequency,
    weekday: automation.weekday,
    monthly_mode: automation.monthly_mode,
    week_ordinal: automation.week_ordinal,
    day_of_month: automation.day_of_month,
    notification_emails: automation.notification_emails,
    translations: draftsForSetup(automation.default_translations, automation.translations),
  })

  useBlockAutoReload(isDirty)

  const schedule: AutomationSchedule = {
    frequency: data.frequency,
    weekday: data.weekday,
    monthly_mode: data.monthly_mode,
    week_ordinal: data.week_ordinal,
    day_of_month: data.day_of_month,
  }

  const updateSchedule = (patch: Partial<AutomationSchedule>) => {
    if (patch.frequency !== undefined) setData("frequency", patch.frequency)
    if (patch.weekday !== undefined) setData("weekday", patch.weekday)
    if (patch.monthly_mode !== undefined) setData("monthly_mode", patch.monthly_mode)
    if (patch.week_ordinal !== undefined) setData("week_ordinal", patch.week_ordinal)
    if (patch.day_of_month !== undefined) setData("day_of_month", patch.day_of_month)
  }

  const zoneOptions = React.useMemo(
    () =>
      automation.time_zone_options.map((option) => ({
        value: option.value,
        label: `${option.label} (${option.offset})`,
      })),
    [automation.time_zone_options],
  )

  // Refetched when the targets change, or a condition's field, comparison or
  // value does — never while the wording is being typed.
  const preview = useAudiencePreview({
    automationId: automation.id,
    coversAllLocals: data.covers_all_locals,
    selectedTargetIds: data.selected_target_ids,
    message: data.custom_message,
  })

  // The server refuses the same things; blocking here saves the round trip.
  const blocked =
    messageBlocksSave(data.custom_message, automation.variables) ||
    languagesBlockSave(data.translations, automation.variables)

  // Whether the picker has moved the schedule off the zone it is stored in.
  const reanchoring = data.time_zone !== automation.time_zone
  // "Eastern Time (US & Canada)" for whatever is currently picked — never a
  // bare IANA identifier, and never the viewer's zone standing in for it now
  // that the zone is chosen outright rather than inherited from the editor.
  const anchorZoneLabel =
    automation.time_zone_options.find((option) => option.value === data.time_zone)
      ?.label ?? data.time_zone
  const anchorIsElsewhere = data.time_zone !== automation.viewer_time_zone
  const description = `Set up ${automation.name} for ${automation.organization_name}. These settings are shared with everyone in ${automation.organization_name}.`

  // A repeatable automation's setups are told apart by their label, so the
  // heading has to carry it — twenty pages all titled "Meeting Reminder" is
  // twenty ways to edit the wrong one.
  const heading = automation.label
    ? `${automation.name} — ${automation.label}`
    : automation.name
  const showLabelField = automation.label_required || Boolean(automation.label)

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (blocked || readOnly) return

    // Nest explicitly rather than posting the fields flat and letting Rails'
    // ParamsWrapper gather them up. The wrapper keeps only keys that are real
    // database columns, and selected_target_ids is a virtual attribute — so a
    // flat post arrived with every Local silently missing and the save failed
    // with "pick at least one" no matter how many were ticked.
    transform((form) => ({
      automation_setting: { ...form, translations: setupPayload(form.translations) },
    }))
    patch(
      automation.setup_id
        ? `/automations/${automation.id}/setup/${automation.setup_id}`
        : `/automations/${automation.id}/setup`,
    )
  }

  return (
    <>
      <Head title={`${heading} setup`}>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${heading} setup`} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <Link
          href="/automations"
          className="mb-4 inline-flex items-center gap-1.5 text-xs text-ink-muted no-underline hover:text-ink-display"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All automations
        </Link>

        <PageHeader title={heading} description={automation.description} />

        <FlashNotice />

        {readOnly && (
          <div className="callout callout-signal mt-6" role="status">
            <p className="text-sm">
              You have read-only access to Automations. You can read every setting
              here and preview who it reaches, but not change anything.{" "}
              {automation.is_enabled
                ? `It is switched on for ${automation.organization_name}`
                : `It is switched off for ${automation.organization_name}`}
              {automation.is_rehearsal ? ", in Rehearsal." : ", and Live."}
            </p>
          </div>
        )}

        <form onSubmit={submit} className="mt-8 space-y-10">
          {/* A disabled fieldset disables every native control inside it at
              once — inputs, selects, the pickers' trigger buttons — so nothing
              below needs its own read-only branch just to stop an edit. Controls
              that would only be dead buttons are hidden by the readOnly props. */}
          <fieldset disabled={readOnly} className="m-0 min-w-0 space-y-10 border-0 p-0">
          {!readOnly && (
            <CheckboxOption
              checked={data.is_enabled}
              onChange={(event) => setData("is_enabled", event.target.checked)}
              label={`Turn this on for ${automation.organization_name}. Switching it off stops future runs and leaves the history alone.`}
            />
          )}

          <RehearsalMode
            rehearsal={data.is_rehearsal}
            onChange={(rehearsal) => setData("is_rehearsal", rehearsal)}
            organizationName={automation.organization_name}
            globalRehearsal={automation.dry_run}
            latestRehearsalRunId={automation.latest_rehearsal_run_id}
            readOnly={readOnly}
          />

          {/* Only asked for once there is more than one setup of the same
              automation to tell apart. On a lone setup the automation's own
              name says everything, and a second field would be noise. */}
          {showLabelField && (
            <div className="space-y-2">
              <label htmlFor="label">Name for this setup</label>
              <Input
                id="label"
                className="max-w-md"
                value={data.label}
                maxLength={60}
                placeholder="LU 12 — Tuesday meeting"
                onChange={(event) => setData("label", event.target.value)}
                aria-invalid={Boolean(errors.label)}
              />
              {errors.label ? (
                <p className="text-xs text-danger-display">{errors.label}</p>
              ) : (
                <p className="text-xs text-ink-muted">
                  {automation.organization_name} has{" "}
                  {automation.sibling_count === 1
                    ? "one other setup"
                    : `${automation.sibling_count} other setups`}{" "}
                  of {automation.name}. This name is how they are told apart in
                  the list and in History.
                </p>
              )}
            </div>
          )}

          <section className="space-y-3">
            <div>
              <h2>Who it reaches</h2>
              <p className="mt-1 text-sm text-ink-muted">
                {automation.name} decides who is eligible.{" "}
                {automation.can_narrow_targets
                  ? `This decides which of ${automation.organization_name}'s ${automation.target_noun}s it is allowed to reach.`
                  : `Everyone eligible in ${automation.organization_name} receives it.`}
              </p>
            </div>

            {/* A Local is a leaf — it reaches its own members and has nothing
                beneath it, so a picker would offer an empty list. */}
            {automation.can_narrow_targets ? (
              <TargetPicker
                targets={automation.available_targets}
                targetNoun={automation.target_noun}
                coversWholeRoster={automation.covers_whole_roster}
                coversAll={data.covers_all_locals}
                selectedIds={data.selected_target_ids}
                organizationName={automation.organization_name}
                error={errors.selected_target_ids}
                onCoversAllChange={(value) => setData("covers_all_locals", value)}
                onSelectedIdsChange={(ids) => setData("selected_target_ids", ids)}
              />
            ) : (
              <div className="callout py-3">
                <p className="text-xs text-ink-body">
                  {automation.organization_name} has no{" "}
                  {automation.target_noun}s beneath it, so this reaches its own
                  members and there is nothing further to narrow to.
                </p>
              </div>
            )}

            {/* Covering IUPAT is the whole union, and two hard limits sit in
                front of that. Say which applies before it is saved. */}
            {automation.covers_whole_roster && data.covers_all_locals && (
              <UnionWideNotice
                preview={preview}
                sendCeiling={automation.send_ceiling}
                maxMatched={automation.max_matched}
                dryRun={automation.dry_run || data.is_rehearsal}
              />
            )}
          </section>

          <section className="space-y-3">
            <div>
              <h2>What it says</h2>
              <p className="mt-1 text-sm text-ink-muted">
                Starts from the wording the automation ships with. Clear the box
                or press Reset to go back to it. A member whose preferred language
                has its own version below is sent that version instead.
              </p>
            </div>
            <MessageEditor
              label="English Message"
              value={data.custom_message}
              resetTo={automation.default_message}
              variables={automation.variables}
              error={errors.custom_message}
              readOnly={readOnly}
              onChange={(value) => setData("custom_message", value)}
            />
            <AudiencePreview
              message={data.custom_message}
              preview={preview}
              organizationName={automation.organization_name}
            />

            <LanguageVersions
              drafts={data.translations}
              onChange={(drafts) => setData("translations", drafts)}
              variables={automation.variables}
              languageOptions={language_options}
              max={automation.max_translations}
              errors={errors}
              readOnly={readOnly}
              renderPreview={(draft) => (
                <LanguagePreview
                  automation={automation}
                  draft={draft}
                  coversAllLocals={data.covers_all_locals}
                  selectedTargetIds={data.selected_target_ids}
                />
              )}
            />
          </section>

          <section className="space-y-3">
            <div>
              <h2>When it sends</h2>
              <p className="mt-1 text-sm text-ink-muted">
                {automation.organization_name} starts from the schedule the
                automation ships with and can change it freely. The time is a
                wall-clock time — the app keeps 9:00 AM at 9:00 AM across a
                daylight-saving change.
              </p>
            </div>

            <ScheduleFields
              value={schedule}
              onChange={updateSchedule}
              errors={{
                frequency: errors.frequency,
                weekday: errors.weekday,
                monthly_mode: errors.monthly_mode,
                week_ordinal: errors.week_ordinal,
                day_of_month: errors.day_of_month,
              }}
            />

            <div className="flex flex-wrap items-start gap-4">
              <div className="space-y-2">
                <label htmlFor="send_time">Send time</label>
                <Input
                  id="send_time"
                  type="time"
                  className="max-w-40"
                  value={data.send_time}
                  onChange={(event) => setData("send_time", event.target.value)}
                  aria-invalid={Boolean(errors.send_time)}
                />
              </div>

              {/* Picking the zone outright is the point: somebody in Eastern
                  setting up a West Coast District Council types 6:00 PM and
                  chooses Pacific rather than doing the arithmetic. */}
              <div className="w-full max-w-sm space-y-2">
                <label htmlFor="time_zone">Time zone</label>
                <Combobox
                  id="time_zone"
                  options={zoneOptions}
                  value={data.time_zone}
                  onChange={(value) => setData("time_zone", value)}
                  placeholder="Select a time zone…"
                  searchPlaceholder="Search time zones…"
                  triggerLabel="Time zone"
                />
              </div>
            </div>

            <p className="text-xs text-ink-muted">
              Currently runs {automation.next_run_label}
            </p>

            {errors.send_time && (
              <p className="text-xs text-danger-display">{errors.send_time}</p>
            )}
            {errors.time_zone && (
              <p className="text-xs text-danger-display">{errors.time_zone}</p>
            )}

            {/* The zone is never re-anchored silently. Saying so matters most
                when it is not the reader's own: an Eastern user fixing a typo
                must not move a Pacific DC's 9:00 AM to 6:00 AM. */}
            {anchorIsElsewhere && (
              <div className="callout callout-signal py-3">
                <p className="text-xs text-ink-body">
                  This schedule is anchored to {anchorZoneLabel}, not to your own
                  time zone ({automation.viewer_time_zone_label}). It stays where
                  it is unless you change it above.
                </p>
              </div>
            )}

            {reanchoring && (
              <p className="text-xs text-ink-muted">
                Saving will re-anchor this schedule from{" "}
                {automation.time_zone_label} to {anchorZoneLabel}.
              </p>
            )}
          </section>

          {/* Who hears how it went. The list belongs to this setup, not to the
              organization: a District Council running one automation for twelve
              Locals sends each Local's results to that Local's own agent. */}
          <section className="space-y-3">
            <div>
              <h2>Delivery notification</h2>
              <p className="mt-1 text-sm text-ink-muted">
                These addresses are emailed a summary every time this runs —
                how many members were texted, how many were not and why, and a
                link to the full results in the Portal. Rehearsals are emailed
                too and are marked as rehearsals. The email carries no member
                names and no phone numbers, so it is safe to forward.
              </p>
            </div>

            <NotificationRecipients
              emails={data.notification_emails}
              max={automation.max_notification_recipients}
              error={errors.notification_emails}
              readOnly={readOnly}
              onChange={(emails) => setData("notification_emails", emails)}
            />
          </section>
          </fieldset>

          <div className="flex items-center gap-3 border-t border-hairline pt-6">
            {readOnly ? (
              <Button variant="secondary" asChild>
                <Link href="/automations">Back to automations</Link>
              </Button>
            ) : (
              <>
                <Button type="submit" disabled={processing || blocked}>
                  {processing ? "Saving…" : "Save"}
                </Button>
                <Button variant="ghost" asChild>
                  <Link href="/automations">Cancel</Link>
                </Button>
              </>
            )}
            {automation.last_changed_by && (
              <p className="text-xs text-ink-muted">
                Last changed by {automation.last_changed_by}
              </p>
            )}
          </div>
        </form>
      </AppShell>
    </>
  )
}

/**
 * One language's preview, standing on a member who prefers that language. A
 * component of its own so each language keeps its own request.
 */
function LanguagePreview({
  automation,
  draft,
  coversAllLocals,
  selectedTargetIds,
}: {
  automation: AutomationSetup
  draft: LanguageDraft
  coversAllLocals: boolean
  selectedTargetIds: number[]
}) {
  const preview = useAudiencePreview({
    automationId: automation.id,
    coversAllLocals,
    selectedTargetIds,
    message: draft.message,
    language: draft.language,
  })

  return (
    <AudiencePreview
      message={draft.message}
      preview={preview}
      organizationName={automation.organization_name}
      language={draft.language}
    />
  )
}
